import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ColumnDataType, ColumnId } from '@blaze-shared/api-contracts-domain';
import {
  ContainerElementMetaUiFeatures,
  getBackground,
  makeFontStyles,
  makeFontStyleStyles,
} from '@blaze-shared/elements-domain';
import {
  BlazeTableSchema,
  DatasetRow,
  ElementSchema,
  findColumn,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { CountdownLabelsPosition, CountdownTemplate } from './countdown-timer.props-model';
import {
  DURATION_DAY,
  DURATION_HOUR,
  getCountdownDataFieldValue,
  getCountdownDurationValue,
  getRemainingSeconds,
  toStringWithLeading0,
} from './countdown-timer.utils';

const COUNTDOWN_INTERVAL = 250;
const COUNTDOWN_UPDATE_TIMEOUT = 500;

@UntilDestroy()
@Component({
  selector: 'be-countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconsModule, ElementLabelComponent],
  host: { class: 'w-full' },
})
export class CountdownTimerComponent
  extends CanvasElementBaseComponent<'countdownTimer'>
  implements CanvasElementComponent<'countdownTimer'>, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  protected readonly clockSvg = viewChild<ElementRef<SVGSVGElement>>('svg');

  private readonly dataValue = this.getDataInput('value');
  private readonly currentValue = signal(0);

  private tickInterval: ReturnType<typeof setInterval> | undefined | null = undefined;
  private tickTimeout: ReturnType<typeof setTimeout> | undefined | null = undefined;
  private tickDeadlineMs = 0;
  private resizeObserver: ResizeObserver | undefined;

  private targetValue = 0;

  private dataValueField?: string;
  private dataSchema?: BlazeTableSchema;
  private dataValueType?: ColumnDataType;
  private dataRowValue?: DatasetRow;

  protected readonly labelEnabled = signal(false);
  protected readonly labelText = signal('');
  protected readonly icon = signal('');

  protected readonly wrapperStyles = signal<Record<string, string | undefined>>({});
  protected readonly controlStyles = signal<Record<string, string | undefined>>({});

  protected readonly duration = signal<ValueWithUnitOfMeasure>('30min');
  protected readonly template = signal<CountdownTemplate>('block');
  protected readonly showDay = signal<boolean>(true);
  protected readonly showHrs = signal<boolean>(true);
  protected readonly showMin = signal<boolean>(true);
  protected readonly showSec = signal<boolean>(true);
  protected readonly showDividers = signal<boolean>(true);
  protected readonly dividersStyle = signal<string>('');
  protected readonly labelsPosition = signal<CountdownLabelsPosition>('top');

  protected readonly displayDay = signal(0);
  protected readonly displayHrs = signal(0);
  protected readonly displayMin = signal('0');
  protected readonly displaySec = signal('0');

  protected readonly stylesDay = signal({});
  protected readonly stylesTime = signal({});
  protected readonly stylesTimeLabel = signal({});
  protected readonly stylesTimeValue = signal({});

  protected readonly clockRadius = signal(0);
  protected readonly clockValuesDash = signal(0);
  protected readonly clockValuesOffset = signal(0);

  protected readonly templateGridClass = computed(() => {
    const timeBlocksCt = Number(this.showHrs()) + Number(this.showMin()) + Number(this.showSec());
    const result = `tpl-${this.template()}-d${Number(this.showDay())}-t${timeBlocksCt}`;
    return result;
  });

  get schema(): ElementSchema | undefined {
    return this.getEventSchema('action');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['props']) {
      const { content } = this.props ?? {};
      const { title, data, options } = content ?? {};

      this.labelEnabled.set(title?.enabled ?? true);
      this.labelText.set(title?.label ?? '');

      const template = options?.template ?? 'block';
      if (template !== this.template()) {
        this.template.set(template);
        if (template === 'clock') {
          setTimeout(() => this.setupResizeObserver());
        } else {
          this.resizeObserver?.disconnect();
        }
      }
      this.duration.set(options?.duration ?? '30min');
      this.showDividers.set(options?.showDividers ?? true);
      const show = options?.show ?? [];
      this.showDay.set(show.includes('days'));
      this.showHrs.set(show.includes('hrs'));
      this.showMin.set(show.includes('min'));
      this.showSec.set(show.includes('s'));
      this.labelsPosition.set(options?.labelsPosition ?? 'bottom');
      this.dataValueField = this.schemaService.getColumnName(data?.targetField);

      if (!this.bindingResults?.['value']) {
        this.updateTarget(getCountdownDurationValue(options?.duration));
        this.updateValue(this.targetValue);
      } else {
        this.updateValueFromDataSource();
      }
      this.updateStyles();
      if (this.tickInterval === null) {
        // has already run to 0 → restart
        this.setupTick();
      }
    }
    if (!this.schema) {
      this.setDefaultEventSchema('action');
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title }) => {
        this.labelText.set(title ?? '');
        safeDetectChanges(this.cdr);
      });

    this.dataValue.schema$.pipe(untilDestroyed(this)).subscribe((schema) => {
      if (!schema) {
        return;
      }
      this.dataSchema = schema as BlazeTableSchema;
    });

    this.dataValue.data$.pipe(untilDestroyed(this)).subscribe((data) => {
      const value = data && Array.isArray(data) ? data[0] : data;
      this.dataRowValue = value;
      this.updateValueFromDataSource();
      this.setupTick();
    });
  }

  ngAfterViewInit(): void {
    if (!this.bindingResults?.['value']) {
      this.setupTick();
    }
    if (this.template() === 'clock') {
      this.setupResizeObserver();
    }
  }

  ngOnDestroy(): void {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
    }
    if (this.tickTimeout) {
      clearTimeout(this.tickTimeout);
    }
    this.resizeObserver?.disconnect();
  }

  private setupTick(): void {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    this.tickInterval = setInterval(() => {
      const remaining = getRemainingSeconds(this.tickDeadlineMs);
      this.updateValue(remaining);

      if (remaining === 0) {
        if (this.tickInterval) {
          clearInterval(this.tickInterval);
        }
        this.tickInterval = null;
        this.tickTimeout = setTimeout(() => this.updateOutput(), COUNTDOWN_UPDATE_TIMEOUT);
      }
    }, COUNTDOWN_INTERVAL);
  }

  private setupResizeObserver(): void {
    const svgEl = this.clockSvg()?.nativeElement;
    this.resizeObserver?.disconnect();

    if (!svgEl || this.template() !== 'clock') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      this.clockRadius.set(Math.min(width, height) / 2);
      this.updateDisplay();
    });
    this.resizeObserver.observe(svgEl);
  }

  private updateValueFromDataSource(): void {
    this.dataValueType = findColumn(this.dataSchema?.columns, {
      id: this.props?.content.data.targetField as ColumnId,
      name: this.dataValueField,
    })?.dataType;

    const targetValueRaw = this.dataValueField
      ? (this.dataRowValue?.[this.dataValueField] ?? 0)
      : 0;
    this.updateTarget(getCountdownDataFieldValue(targetValueRaw, this.dataValueType));
    this.updateValue(this.targetValue);
  }

  private updateValue(value: number): void {
    const rounded = Math.round(value);
    if (this.currentValue() === rounded) {
      return;
    }
    this.currentValue.set(rounded);
    this.updateDisplay();
  }

  private updateTarget(value: number): void {
    this.targetValue = value;
    this.tickDeadlineMs = Date.now() + value * 1000;
  }

  private updateDisplay(): void {
    let total = this.currentValue();
    const days = Math.floor(total / DURATION_DAY);
    total -= days * DURATION_DAY;
    const hours = Math.floor(total / DURATION_HOUR);
    total -= hours * DURATION_HOUR;
    const mins = Math.floor(total / 60);
    total -= mins * 60;
    this.displayDay.set(days);
    this.displayHrs.set(hours);
    this.displayMin.set(toStringWithLeading0(mins, 2));
    this.displaySec.set(toStringWithLeading0(total, 2));

    if (this.template() === 'clock') {
      const percent = this.currentValue() / this.targetValue;
      const circ = 2 * Math.PI * this.clockRadius();
      const offset = circ * (1 - percent);
      this.clockValuesDash.set(circ);
      this.clockValuesOffset.set(offset);
    }
  }

  private updateStyles(): void {
    const { general, day, time, label, clock } = this.props?.style || {};
    if (!general || !day || !time || !label || !clock) {
      return;
    }

    this.wrapperStyles.set({
      // Margin is rendered as wrapper padding here instead of the base
      // initMarginHandler, which would add a theme fallback this element never had
      padding: general.offset?.margin,
    });

    if (this.template() === 'clock') {
      this.controlStyles.set({
        ['--countdown-gutter-color']: clock.gutter.color1,
        ['--countdown-active-color']: clock.active.color1,
        ['--countdown-track-width']: clock.width,
      });

      this.stylesTimeLabel.set({
        color: label.value.color1,
        ...makeFontStyles(label, undefined, { lineHeight: true }),
        ...makeFontStyleStyles(label.fontStyle),
        ['font-weight']: this.fontsService.getFontWeight(label.fontFamily, label.fontWeight),
      });
      this.stylesTimeValue.set({
        ...this.stylesTimeLabel(),
        color: label.divider.color1,
      });
    } else {
      this.controlStyles.set({
        ['--countdown-dividers-width']: time.dividerWidth,
        ['--countdown-dividers-color']: time.divider.color1,
      });

      this.stylesDay.set({
        color: day.value.color1,
        background: getBackground(day.fill),
        border:
          day.border.selectorType !== 'none' && day.borderWidth !== '0px'
            ? `${day.borderWidth} solid ${day.border.color1}`
            : undefined,
        borderRadius: day.radius !== '0px' ? day.radius : undefined,

        ...makeFontStyles(day, undefined, { lineHeight: true }),
        ...makeFontStyleStyles(day.fontStyle),
        ['font-weight']: this.fontsService.getFontWeight(day.fontFamily, day.fontWeight),
      });

      this.stylesTimeLabel.set({
        color: time.label.value.color1,
        ...makeFontStyles(time.label, undefined, { lineHeight: true }),
        ...makeFontStyleStyles(time.label.fontStyle),
        ['font-weight']: this.fontsService.getFontWeight(
          time.label.fontFamily,
          time.label.fontWeight,
        ),
      });
      this.stylesTimeValue.set({
        color: time.value.value.color1,
        ...makeFontStyles(time.value, undefined, { lineHeight: true }),
        ...makeFontStyleStyles(time.value.fontStyle),
        ['font-weight']: this.fontsService.getFontWeight(
          time.value.fontFamily,
          time.value.fontWeight,
        ),
      });

      const stylesTime: Record<string, any> = {
        background: getBackground(time.fill),
        border:
          time.border.selectorType !== 'none' && time.borderWidth !== '0px'
            ? `${time.borderWidth} solid ${time.border.color1}`
            : undefined,
        borderRadius: time.radius !== '0px' ? time.radius : undefined,
      };
      const pos = this.labelsPosition();
      if (pos === 'top' || pos === 'bottom') {
        stylesTime['flex-direction'] = pos === 'top' ? 'column-reverse' : 'column';
        stylesTime['gap'] = '4px';
      } else {
        stylesTime['gap'] = '8px';
        if (pos === 'left') {
          stylesTime['flex-direction'] = 'row-reverse';
        }
      }
      this.stylesTime.set(stylesTime);
    }
  }

  private updateOutput(): void {
    const workflowId = this.props?.content.data.action?.workflowId;
    if (!this.schema || !workflowId) {
      return;
    }
    this.output.emit({
      eventType: 'action',
      data: { data: null, workflowId },
      isInitialData: false,
      output: 'events',
      schema: this.schema,
    });
  }
}
